import React from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Link as RouterLink } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import clsx from 'classnames';
import { useHistory } from 'react-router-dom';

export interface ProductOption {
  title?: string;
  description?: string;
  imgUrl?: string;
  link?: string;
}

const useStyles: any = makeStyles(
  (theme: Theme) =>
    createStyles({
      root: {
        borderRadius: '6px',
        height: '100%',
      },
      area: {
        padding: theme.spacing(1),
        color: 'transparent',
      },
      responsiveArea: {
        [theme.breakpoints.down('md')]: {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
        },
      },
      horizontal: {
        padding: theme.spacing(1),
        color: 'transparent',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
      },
      noClick: {
        cursor: 'initial',
      },
      media: {
        // aspectRatio: '286/200', //not working for crossbrowser
        '&::before': {
          float: 'left',
          paddingTop: '63.08%',
          content: "''",
        },
        '&::after': {
          display: 'block',
          content: "''",
          clear: 'both',
        },

        borderRadius: '6px',
        width: '100%',
      },
      responsiveMedia: {
        [theme.breakpoints.down('md')]: {
          width: '50%',
          height: 114,
        },
      },
      content: {
        paddingBottom: theme.spacing(1),
        width: '100%',
        color: theme.palette.primary.main,
      },
      responsiveContent: {
        [theme.breakpoints.down('md')]: {
          width: '50%',
          paddingTop: 0,
        },
      },
      button: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
      action: {
        justifyContent: 'flex-end',
        [theme.breakpoints.down('md')]: {
          alignSelf: 'stretch',
          alignItems: 'flex-end',
        },
      },
      horizontalAction: {
        alignSelf: 'stretch',
        alignItems: 'flex-end',
        paddingRight: 0,
      },
      unselected: {
        opacity: 0.3,
      },
      clickableMedia: {
        width: '100%',
        height: 160,
      },
      responsiveClickableMedia: {
        [theme.breakpoints.down('md')]: {
          width: 136,
          height: 98,
        },
      },
      clickableContent: {
        width: '100%',
      },
      horizontalMedia: {
        height: 85,
        [theme.breakpoints.up('sm')]: {
          width: 200,
          height: 85,
        },
      },
      horizontalContent: {
        padding: theme.spacing(0, 0, 0, 2),
        width: '100%',
        '& > .MuiTypography-h3': {
          fontSize: 19,
          marginBottom: 0,
        },
        '& > .MuiTypography-body1': {
          fontSize: 16,
        },
      },
    }),
  { name: 'ProductOptionGridItem' },
);

const ProductOptionGridItem: React.FC<{
  item: ProductOption;
  clickableProps?: any; // for card type on corporate
  showArrow?: boolean;
  horizontal?: boolean; // for physical card design options
  responsive?: boolean;
  noCursor?: boolean;
}> = ({
  item,
  clickableProps = null,
  showArrow = false,
  horizontal = false,
  responsive = true,
  noCursor = false,
  // @ts-ignore
  className,
  ...rest
}) => {
  const styles = useStyles();
  const history = useHistory();
  const { selected, handleSelect } = clickableProps || {};
  const cardProps = !!clickableProps
    ? {
        className: clsx(styles.root, (!selected || history.location.search) && styles.unselected, className),
        onClick: handleSelect,
      }
    : {
        className: clsx(styles.root, className),
      };

  return (
    <Card {...cardProps} {...rest}>
      <CardActionArea
        className={clsx(styles.area, {
          [styles.noClick]: noCursor,
          [styles.horizontal]: horizontal,
          [styles.reponsiveArea]: responsive,
        })}
      >
        <CardMedia
          className={clsx(
            styles.media,
            responsive && styles.responsiveMedia,
            horizontal && styles.horizontalMedia,
            !!clickableProps && styles.clickableMedia,
            !!clickableProps && responsive && styles.responsiveClickableMedia,
          )}
          image={item.imgUrl}
          title={item.title}
        />
        <CardContent className={clsx(styles.content, responsive && styles.responsiveContent, horizontal && styles.horizontalContent, !!clickableProps && styles.clickableContent)}>
          <Typography gutterBottom variant="h3" component="div">
            {item.title}
          </Typography>
          <Typography variant="body1" component="p">
            {item.description}
          </Typography>
        </CardContent>
        {showArrow && (
          <CardActions className={clsx(styles.action, horizontal && styles.horizontalAction)}>
            <IconButton className={styles.button} size="small" aria-label="select option" component={RouterLink} to={item.link || '#option'}>
              <ArrowForwardIcon />
            </IconButton>
          </CardActions>
        )}
      </CardActionArea>
    </Card>
  );
};

export default ProductOptionGridItem;
